import React from 'react';
import { motion, MotionConfig, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
};

const inView = {
  initial: 'hidden' as const,
  whileInView: 'show' as const,
  viewport: { once: true, margin: '-80px' },
};

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
    {children}
  </span>
);

const DEBATE_PHASES = [
  {
    n: '01',
    phase: 'Research',
    label: 'AI preparation',
    description:
      'Both AI agents independently gather evidence, data, and perspectives — building their strongest possible case before saying a word.',
  },
  {
    n: '02',
    phase: 'Opening statements',
    label: 'Round 1',
    description:
      'Each agent presents its core argument clearly and directly, establishing its position with logic rather than rhetoric.',
  },
  {
    n: '03',
    phase: 'Rebuttals',
    label: 'Round 2',
    description:
      'Agents engage directly with the opposing argument — exposing weaknesses and reinforcing their own case with evidence.',
  },
  {
    n: '04',
    phase: 'Closing arguments',
    label: 'Round 3',
    description:
      'A final opportunity to distill the debate to its essence and make the most compelling case before voting opens.',
  },
  {
    n: '05',
    phase: 'Summary & voting',
    label: 'Final step',
    description:
      'AI generates balanced summaries of both positions. You read, reflect, and vote on which side argued more persuasively.',
  },
];

const HowItWorks: React.FC = () => {
  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate min-h-screen w-full bg-white">
        {/* Blueprint grid */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <div className="relative z-10">

          {/* ── Hero ── */}
          <section className="mx-auto max-w-5xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20 text-center">
            <motion.div initial="hidden" animate="show" variants={stagger}>
              <motion.div variants={fadeUp}>
                <Eyebrow>How it works</Eyebrow>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                className="mt-4 text-balance font-serif text-5xl leading-tight tracking-tight text-charcoal sm:text-6xl"
              >
                The thinking behind
                <br className="hidden sm:block" /> the debate.
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="mx-auto mt-5 max-w-2xl text-pretty text-lg leading-relaxed text-ink-muted sm:text-xl"
              >
                Steelman forces both sides to argue at their absolute best. No strawmen,
                no misrepresentations — just the strongest version of each position,
                argued with equal rigor by AI.
              </motion.p>
            </motion.div>
          </section>

          {/* ── The method — 3 principles ── */}
          <section className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <motion.div {...inView} variants={fadeUp} className="text-center">
              <Eyebrow>The Steelman method</Eyebrow>
              <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                Three non-negotiables
              </h2>
            </motion.div>

            <motion.div
              {...inView}
              variants={stagger}
              className="mt-12 grid grid-cols-1 gap-x-10 gap-y-12 md:grid-cols-3"
            >
              {[
                {
                  n: '01',
                  title: 'Best arguments only',
                  body: 'Every position is presented at its absolute strongest — backed by logic, evidence, and the most charitable interpretation.',
                },
                {
                  n: '02',
                  title: 'Zero bias',
                  body: 'No misrepresentations, no hidden agendas. Both sides get the same care, the same rigor, the same word count.',
                },
                {
                  n: '03',
                  title: 'Pure logic',
                  body: 'Reasoning and evidence over emotion and rhetoric. The debate earns its conclusion rather than performing it.',
                },
              ].map(item => (
                <motion.div key={item.n} variants={fadeUp}>
                  <div className="font-serif text-3xl text-charcoal/20">{item.n}</div>
                  <div className="mt-4 h-px w-10 bg-gradient-to-r from-steel-blue to-vermilion opacity-60" />
                  <h3 className="mt-5 text-lg font-semibold text-charcoal">{item.title}</h3>
                  <p className="mt-2 text-pretty text-[15px] leading-relaxed text-ink-muted">
                    {item.body}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </section>

          {/* ── Debate structure ── */}
          <section className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <motion.div {...inView} variants={fadeUp}>
              <Eyebrow>Debate structure</Eyebrow>
              <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                Five phases, start to vote
              </h2>
            </motion.div>

            <motion.div
              {...inView}
              variants={stagger}
              className="mt-10 divide-y divide-black/[0.06] overflow-hidden rounded-2xl border border-black/[0.08] bg-white/85 backdrop-blur-sm"
            >
              {DEBATE_PHASES.map(item => (
                <motion.div
                  key={item.n}
                  variants={fadeUp}
                  className="flex items-start gap-6 px-6 py-7 transition-colors hover:bg-black/[0.02] sm:px-8"
                >
                  <div className="w-8 flex-shrink-0 font-serif text-xl text-charcoal/20 pt-0.5">
                    {item.n}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="text-base font-semibold text-charcoal">{item.phase}</h3>
                      <span className="rounded-full border border-hairline px-2.5 py-0.5 font-mono text-[10px] text-ink-muted">
                        {item.label}
                      </span>
                    </div>
                    <p className="mt-1.5 text-pretty text-[15px] leading-relaxed text-ink-muted">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </section>

          {/* ── Two ways to engage ── */}
          <section className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <motion.div {...inView} variants={fadeUp} className="mb-12">
              <Eyebrow>Two ways to engage</Eyebrow>
              <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                Create or explore
              </h2>
            </motion.div>

            <motion.div
              {...inView}
              variants={stagger}
              className="grid grid-cols-1 gap-6 md:grid-cols-2"
            >
              {[
                {
                  title: 'Create your own debate',
                  description:
                    'Bring any topic to the Arena. Watch AI argue both sides with full rigor. Private by default — publish to the community whenever you\'re ready.',
                  items: [
                    'Instant generation, any topic',
                    'Private by default',
                    'Publish when ready',
                  ],
                  cta: 'Go to Arena',
                  href: '/arena',
                },
                {
                  title: 'Explore public debates',
                  description:
                    'Browse what the community has shared. Watch, read, and vote on debates that have already been published — no account needed.',
                  items: [
                    'No account needed to watch',
                    'Vote to join the discussion',
                    'Share debates you find compelling',
                  ],
                  cta: 'Browse debates',
                  href: '/explore',
                },
              ].map(block => (
                <motion.div
                  key={block.title}
                  variants={fadeUp}
                  className="group flex flex-col rounded-2xl border border-black/[0.07] bg-white/90 p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-frame-lg"
                >
                  <h3 className="font-serif text-xl font-semibold text-charcoal">
                    {block.title}
                  </h3>
                  <p className="mt-3 text-pretty text-[15px] leading-relaxed text-ink-muted">
                    {block.description}
                  </p>

                  <ul className="mt-6 flex-grow space-y-2.5 border-t border-hairline pt-6">
                    {block.items.map(item => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-charcoal/80">
                        <svg
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-charcoal"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={block.href}
                    className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal transition-colors group-hover:text-steel-blue"
                  >
                    {block.cta}
                    <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </section>

          {/* ── Voting steps + widget ── */}
          <section className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <motion.div {...inView} variants={fadeUp} className="text-center">
              <Eyebrow>Voting</Eyebrow>
              <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                You decide who won
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-pretty text-lg leading-relaxed text-ink-muted">
                After reading both sides and the AI summary, vote for whichever case you found more persuasive.
              </p>
            </motion.div>

            <motion.div
              {...inView}
              variants={stagger}
              className="mt-12 grid grid-cols-1 gap-x-10 gap-y-12 md:grid-cols-3"
            >
              {[
                { n: '01', title: 'Read both sides', body: 'Every argument from the supporting and opposing positions, in full.' },
                { n: '02', title: 'Review the summary', body: 'An AI-generated digest of the key points from each side.' },
                { n: '03', title: 'Cast your vote', body: 'Choose the stronger argument. See how the community voted in real time.' },
              ].map(step => (
                <motion.div key={step.n} variants={fadeUp}>
                  <div className="font-serif text-3xl text-charcoal/20">{step.n}</div>
                  <div className="mt-4 h-px w-10 bg-gradient-to-r from-steel-blue to-vermilion opacity-60" />
                  <h3 className="mt-5 text-lg font-semibold text-charcoal">{step.title}</h3>
                  <p className="mt-2 text-pretty text-[15px] leading-relaxed text-ink-muted">{step.body}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Vote widget — the one legitimate place for steel-blue / vermilion */}
            <motion.div
              {...inView}
              variants={fadeUp}
              className="mx-auto mt-14 max-w-lg"
            >
              <div className="overflow-hidden rounded-2xl border border-black/[0.07] bg-white/90 shadow-frame backdrop-blur-sm">
                <div className="border-b border-black/[0.06] px-6 py-4">
                  <p className="font-mono text-xs text-ink-muted">
                    Sample vote
                  </p>
                  <p className="mt-1 text-sm font-medium text-charcoal">
                    Which side presented the stronger argument?
                  </p>
                </div>
                <div className="grid grid-cols-2 divide-x divide-black/[0.06]">
                  <button className="group px-6 py-5 text-left transition-colors hover:bg-steel-blue/5">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="h-3 w-1 rounded-full bg-steel-blue" />
                      <span className="font-mono text-xs font-medium uppercase tracking-wider text-steel-blue">
                        For
                      </span>
                    </div>
                    <p className="text-sm text-charcoal/70">Pro-Regulation</p>
                  </button>
                  <button className="group px-6 py-5 text-left transition-colors hover:bg-vermilion/5">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="h-3 w-1 rounded-full bg-vermilion" />
                      <span className="font-mono text-xs font-medium uppercase tracking-wider text-vermilion">
                        Against
                      </span>
                    </div>
                    <p className="text-sm text-charcoal/70">Pro-Innovation</p>
                  </button>
                </div>
                <div className="border-t border-black/[0.06] px-6 py-3">
                  <div className="flex h-1 overflow-hidden rounded-full bg-hairline">
                    <div className="bg-steel-blue" style={{ width: '58%' }} />
                    <div className="ml-auto bg-vermilion" style={{ width: '42%' }} />
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-xs">
                    <span className="font-mono font-medium text-steel-blue">58% For</span>
                    <span className="font-mono text-ink-muted">1,204 votes</span>
                    <span className="font-mono font-medium text-vermilion">42% Against</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>

          {/* ── CTA — floating charcoal panel ── */}
          <section className="px-6 pb-24 pt-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, ease: EASE }}
              className="blueprint-grid-dark relative mx-auto max-w-5xl overflow-hidden rounded-[28px] bg-charcoal px-8 py-20 text-center sm:py-24"
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                  background:
                    'radial-gradient(60% 120% at 15% 0%, rgba(58,104,160,0.45), transparent 60%), radial-gradient(60% 120% at 85% 100%, rgba(229,68,45,0.40), transparent 60%)',
                }}
              />
              <div className="relative">
                <h2 className="text-balance font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl">
                  Ready to get started?
                </h2>
                <p className="mx-auto mt-5 max-w-lg text-pretty text-lg leading-relaxed text-white/60">
                  Create your own debate or explore what the community has already shared.
                </p>
                <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Link
                    to="/arena"
                    className="w-full rounded-full bg-white px-8 py-4 text-base font-medium text-charcoal transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-white/90 sm:w-auto"
                  >
                    Create a debate
                  </Link>
                  <Link
                    to="/explore"
                    className="w-full rounded-full border border-white/25 px-8 py-4 text-base font-medium text-white transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-white/10 sm:w-auto"
                  >
                    Explore debates
                  </Link>
                </div>
              </div>
            </motion.div>
          </section>

        </div>
      </div>
    </MotionConfig>
  );
};

export default HowItWorks;
